import React, { useState } from "react";
import { Alert, Snackbar } from "@mui/material";
import { ForumPostReplyItem } from "../../components/forums/ForumPostReplyItem/ForumPostReplyItem";
import { ForumsPostScreen, IForumsPostScreenProps } from "../../components/forums/ForumsPostScreen/ForumsPostScreen";
import { sendSupportTicket } from "../../services/support";
import { ForumPost, FORUMS_TOPIC_FIELD, WithID } from "../../types/db";
import { ForumRelatedArticlesContainer } from "./ForumRelatedArticlesContainer";
import { ForumRelatedPostsContainer } from "./ForumRelatedPostsContainer";
import { ForumReplyContainer } from "./ForumReplyContainer";
import { useTranslation } from "react-i18next";
import { useForumPost } from "../../hooks/forum/useForumPost";
import { useForumPostReplies } from "../../hooks/forum/useForumPostReplies";
import { auth } from "../../services/firebase";
import { addLikeToPost, addReplyToPost, removeLikeFromPost } from "../../services/forums";

/** Maximum reply per page (for pagination) */
const REPLIES_PER_PAGE = 5;

export interface IForumsPostContainerProps {
    /** The post id to fetch and display */
    postId: string;
    /** Callback for navigation to a certain post */
    navigateToPost: (postId: string) => void;
    /** Callback for navigation to a certain topic */
    navigateToTopic: (topic: string) => void;
    /** Callback for navigation to a certain article */
    navigateToArticle: (articleId: string) => void;
    /** Callback for navigation to an author/user profile */
    navigateToAuthor: (userId: string) => void;
}

/** Container taking care of fetching the data of the post and displaying it */
export const ForumsPostContainer = ({ postId, navigateToPost, navigateToTopic, navigateToArticle, navigateToAuthor }: IForumsPostContainerProps) => {
    const { t } = useTranslation();

    const {
        data: dataPost,
        isLoading: isLoadingPost,
        isError: isErrorPost, } = useForumPost(postId);

    const { data: dataReplies,
        isLoading: isLoadingReplies,
        hasNextPage,
        fetchNextPage, } = useForumPostReplies(postId)

    const [reported, setReported] = useState(false);

    if (isLoadingPost) {
        return <ForumsPostScreen loading />
    }

    if (isErrorPost || !dataPost) {
        return <ForumsPostScreen error={{ message: "", onRetryClick: () => navigateToTopic("") }} />
    }

    const handleReportClick = () => {
        sendSupportTicket({
            type: "report",
            error_message: "reporting",
            feature: "forum",
            document: "post",
            doc_id: postId,
            user_sender: auth.currentUser?.uid || ''
        })
            .then(() => setReported(true))
    }

    return <>
        <ForumsPostScreen
            {...mapPostPropsFromData(
                dataPost,
                { navigateToTopic, navigateToArticle, navigateToPost, navigateToAuthor },
            )}
            replies={isLoadingReplies ?
                new Array(REPLIES_PER_PAGE).fill({}).map((_, index) => <ForumPostReplyItem loading key={`reply-loading-${index}`} />)
                :
                dataReplies?.map(reply => <ForumReplyContainer key={`reply-${reply.id}`} postId={postId} reply={reply} />)
            }
            onLoadMoreReplies={hasNextPage ? fetchNextPage : undefined}
            onReport={handleReportClick}
        />
        <Snackbar autoHideDuration={3000} open={reported} onClose={() => setReported(false)} >
            <Alert onClose={() => setReported(false)} severity="success" sx={{ width: '100%' }}>
                {t('forum.post.reportConfirmMessage')}
            </Alert>
        </Snackbar>
    </>
}


const mapPostPropsFromData = (
    data: WithID<ForumPost> & { isLiked: boolean },
    callbacks: {
        navigateToTopic: (topic: string) => void,
        navigateToPost: (postId: string) => void,
        navigateToArticle: (articleId: string) => void,
        navigateToAuthor: (userId: string) => void,
    },
): IForumsPostScreenProps => {
    const handleLikesClick = () => {
        if (data.isLiked) {
            removeLikeFromPost(data.id);
        } else {
            addLikeToPost(data.id);
        }
    }

    const handleReply = async (content: string) => {
        addReplyToPost(data.id, content)
    }

    return {
        title: data.title,
        timestamp: data.timestamp?.toDate() ?? new Date(Date.now()),
        content: data.content,
        author: {
            avatar: data.author.avatar_url || '',
            displayName: data.author.display_name || '',
            onClick: () => callbacks.navigateToAuthor(data.author.id)
        },
        topic: {
            type: data.topic,
            onClick: () => callbacks.navigateToTopic(data.topic)
        },
        onHomeClick: () => callbacks.navigateToTopic(""),
        likeButton: {
            liked: data.isLiked,
            likesCount: data.likes_count || 0,
            onLikeClick: handleLikesClick,
        },
        repliesCount: data.replies_count || 0,
        onReplyClick: () => { },
        onReply: handleReply,
        currentUserAvatar: auth.currentUser?.photoURL || '',
        relatedPosts: <ForumRelatedPostsContainer
            topic={data[FORUMS_TOPIC_FIELD]}
            postId={data.id}
            navigateToPost={callbacks.navigateToPost}
        />,
        relatedArticles: <ForumRelatedArticlesContainer
            topic={data[FORUMS_TOPIC_FIELD]}
            navigateToArticle={callbacks.navigateToArticle}
        />
    }
}

